import { useEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import {
    IconOptionsReport,
    IconOptionsThrash
} from 'assets/svg/sprite'
import screens from 'styles/screens'

interface StoryOptionsProps {
    isOpen: boolean
    setIsOpen: (setTo: boolean) => void
    handleDelete?: () => void
    handleReport?: () => void
    handleCopyLink?: () => void
    isOwner: boolean
    storyId: string
}

export function StoryOptions({
    isOpen,
    setIsOpen,
    handleDelete,
    handleReport,
    isOwner,
}: StoryOptionsProps) {
    const { t } = useTranslation()
    const optionsRef = useRef<HTMLDivElement>(null)

    const [portalTarget, setPortalTarget] =
        useState<HTMLElement | null>(null)

    useEffect(() => {
        setPortalTarget(document.body)
    }, [])

    if (!isOpen || !portalTarget) {
        return null
    }

    return createPortal(
        <Backdrop
            onMouseDown={e => {
                if (e.target === e.currentTarget) {
                    setIsOpen(false)
                }
            }}
        >
            <OptionsCard
                ref={optionsRef}
                onMouseDown={e => {
                    e.stopPropagation()
                }}
            >
                {isOwner ? (
                    <OptionContainer
                        onClick={() => {
                            handleDelete?.()
                        }}
                    >
                        <IconOptionsThrash />

                        <OptionSpan>
                            {t('deleteStory')}
                        </OptionSpan>
                    </OptionContainer>
                ) : (
                    <OptionContainer
                        onClick={() => {
                            handleReport?.()
                        }}
                    >
                        <IconOptionsReport />

                        <OptionSpan>
                            {t('report')}
                        </OptionSpan>
                    </OptionContainer>
                )}
            </OptionsCard>
        </Backdrop>,
        portalTarget
    )
}

const Backdrop = styled.div`
    position: fixed;
    inset: 0;

    z-index: 999999;

    display: flex;
    align-items: flex-end;
    justify-content: center;

    background: transparent;

    @media (min-width: ${screens.medium}) {
        align-items: center;
        justify-content: center;

        background: rgba(0, 0, 0, 0.25);
    }
`

const OptionsCard = styled.div`
    width: 100%;

    padding: 3rem 0 2rem;

    background: #ffffff;

    border-radius: 2rem 2rem 0 0;

    box-shadow:
        0 -2rem 4rem rgba(17, 29, 41, 0.6);

    @media (min-width: ${screens.medium}) {
        width: calc(100% - 4rem);
        max-width: 400px;

        padding: 2rem 0;

        border-radius: 2.5rem;

        box-shadow:
            0 2rem 6rem rgba(17, 29, 41, 0.35);
    }
`

const OptionSpan = styled.span`
    color: #0d1444;

    font-family: Montserrat, sans-serif;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 600;

    letter-spacing: 0.02em;
    line-height: 1.7rem;

    margin-left: 1.4rem;
`

const OptionContainer = styled.div`
    display: flex;
    align-items: center;

    padding: 1.3rem 3rem 0.8rem;
    margin-bottom: 1rem;

    cursor: pointer;

    &:hover {
        background-color: #f4f4f4;
    }

    &:last-child {
        margin-bottom: 0;
    }
`